import { Breadcrumb } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { DataTableColumnHeader } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableErrorRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import {
  useDataQualScoreCardsQuery,
  useMetaDataScoreCardsQuery,
} from '@/hooks/api/use-datagovernance-dashboard-query'
import { Schema, SchemaQueryParams, useSchemaQuery } from '@/hooks/api/use-schema-query'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { T } from '@tolgee/react'
import { SearchIcon, Target, TrendingUp } from 'lucide-react'
import React from 'react'
import { PageTemplate } from '../_common/page-template'
import { DescriptionCell, NameButton, OwnersCell } from './_common'
import {
  useDataCatalogNavigation,
  useSchemaTableBreadcrumbs,
} from './_common/hooks/use-data-catalog-breadcrumbs'
import { ScoreCardsUpdate } from './dashboard/_components/_common/scorecards-update'
import { getStyles } from './dashboard/utils'

type SchemaTableProps = Readonly<SchemaQueryParams>

function SchemaTable(props: SchemaTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = React.useState('')
  const schemaQuery = useSchemaQuery(props)
  const { openTableTab, openDataGovernanceDashboardTab } = useDataCatalogNavigation()
  const { breadcrumbItems } = useSchemaTableBreadcrumbs(props.database_name, props.assetType)

  const metadataScoreQuery = useMetaDataScoreCardsQuery({
    filters: {
      database: props.database_name,
      schema: 'all',
      table: 'all',
      sensitivity: 'all',
    },
    queryOptions: {
      enabled: true,
    },
  })

  const dataQualityScore = useDataQualScoreCardsQuery({
    filters: {
      database: props.database_name,
      schema: 'all',
      table: 'all',
      sensitivity: 'all',
    },
    queryOptions: {
      enabled: true,
    },
  })

  const schemas = React.useMemo(
    () =>
      schemaQuery.data?.schemas.map((schema) => ({
        ...schema,
        onOpenSchema: () => {
          openTableTab(
            props.database_name,
            schema.schema_name || 'Schema',
            props.assetType || 'database',
          )
        },
      })) ?? [],
    [schemaQuery.data?.schemas, openTableTab, props.database_name],
  )

  const table = useReactTable({
    data: schemas,
    columns: SCHEMA_COLUMN_DEFS,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    sortDescFirst: true,
    globalFilterFn: (row, columnId, value) => {
      // filter by the schema_name column
      const schemaName = row.getValue('schema_name') as string
      return schemaName.toLowerCase().includes(value.toLowerCase())
    },
    state: {
      sorting,
      globalFilter,
    },
    enableColumnFilters: false,
  })

  const { rows } = table.getRowModel()
  const isInitialLoading = schemaQuery.isPending
  const isTableLoading = isInitialLoading && rows.length === 0

  return (
    <div className='flex w-full flex-col gap-6'>
      <PageTemplate.Header hasBreadcrumb className='flex-wrap items-center justify-between gap-4'>
        <div className='space-y-4'>
          <Breadcrumb items={breadcrumbItems} />
          <PageTemplate.H4>
            <T keyName='schema.subtitle' />
          </PageTemplate.H4>
        </div>
        <div className='relative'>
          <SearchIcon className='absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-200' />
          <Input
            placeholder='Search'
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className='w-80 pl-10'
          />
        </div>
      </PageTemplate.Header>

      <div className='flex items-center gap-6'>
        <Button
          unstyled
          className='flex items-center gap-2 rounded-lg p-2 hover:bg-neutral-50 disabled:cursor-not-allowed'
          disabled={metadataScoreQuery.isLoading}
          onClick={() => {
            openDataGovernanceDashboardTab(
              'metaData',
              props.database_name,
              'all',
              'all',
              props.assetType || 'database',
              'all',
            )
          }}
        >
          <div className='flex flex-col items-start gap-1'>
            <div className='flex items-center gap-2'>
              <p className='text-sm font-normal'>Metadata Score</p>
              <TrendingUp className='size-4 text-neutral-200' />
            </div>
            {metadataScoreQuery.isLoading ? (
              <div className='mt-2 h-2 w-24 animate-pulse rounded-full bg-gray-200' />
            ) : (
              <div className='flex items-center gap-2'>
                <span
                  className={`text-sm font-medium ${getStyles(metadataScoreQuery.data?.score || 0).text}`}
                >
                  {metadataScoreQuery.data?.score
                    ? `${Math.round(metadataScoreQuery.data.score)}%`
                    : 'N/A'}
                </span>
                <Progress
                  value={metadataScoreQuery.data?.score || 0}
                  className='h-1 w-16'
                  color={getStyles(metadataScoreQuery.data?.score || 0).progress}
                />
              </div>
            )}
          </div>
        </Button>
        <Button
          unstyled
          className='flex items-center gap-2 rounded-lg p-2 hover:bg-neutral-50 disabled:cursor-not-allowed'
          disabled={dataQualityScore.isLoading}
          onClick={() => {
            openDataGovernanceDashboardTab(
              'dataQuality',
              props.database_name,
              'all',
              'all',
              props.assetType || 'database',
              'all',
            )
          }}
        >
          <div className='flex flex-col items-start gap-1'>
            <div className='flex items-center gap-2'>
              <p className='text-sm font-normal'>Data Quality Score</p>
              <Target className='size-4 text-neutral-200' />
            </div>
            {dataQualityScore.isLoading ? (
              <div className='mt-2 h-2 w-24 animate-pulse rounded-full bg-gray-200' />
            ) : (
              <div className='flex items-center gap-2'>
                <span
                  className={`text-sm font-medium ${getStyles(dataQualityScore.data?.overall_assessment.percentage_score || 0).text}`}
                >
                  {dataQualityScore.data?.overall_assessment.percentage_score
                    ? `${Math.round(dataQualityScore.data?.overall_assessment.percentage_score)}%`
                    : 'N/A'}
                </span>
                <Progress
                  value={dataQualityScore.data?.overall_assessment.percentage_score || 0}
                  className='h-1 w-16'
                  color={
                    getStyles(dataQualityScore.data?.overall_assessment.percentage_score || 0)
                      .progress
                  }
                />
              </div>
            )}
          </div>
        </Button>
      </div>
      {metadataScoreQuery.isFetching || dataQualityScore.isFetching ? <ScoreCardsUpdate /> : <></>}

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{ width: `${header.getSize()}px` }}
                  data-state={
                    (header.column.getIsSorted() || header.column.getIsFiltered()) && 'selected'
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isTableLoading && (
            <TableSkeletonRows rowCount={3} cellCount={SCHEMA_COLUMN_DEFS.length} />
          )}

          {schemaQuery.isError && <TableErrorRow colSpan={SCHEMA_COLUMN_DEFS.length} />}

          {schemaQuery.isSuccess &&
            rows.length > 0 &&
            rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className='h-[72px]'
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{ width: `${cell.column.getSize()}px` }}
                    data-state={
                      (cell.column.getIsSorted() || cell.column.getIsFiltered()) && 'selected'
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}

          {schemaQuery.isSuccess && rows.length === 0 && !schemaQuery.isFetching && (
            <TableRow>
              <TableCell colSpan={SCHEMA_COLUMN_DEFS.length} className='h-24 text-center'>
                <T keyName='schema.table.messages.noSchemas' />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

type SchemaTableRow = Schema & { onOpenSchema: () => void }

const SCHEMA_COLUMN_DEFS: ColumnDef<SchemaTableRow>[] = [
  {
    accessorKey: 'schema_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} titleKey='schema.table.headers.schemaName'>
        <T keyName='schema.table.headers.schemaName' />
      </DataTableColumnHeader>
    ),
    cell: ({
      row: {
        original: { schema_name, onOpenSchema },
      },
    }) => (
      <NameButton
        name={schema_name}
        onClick={onOpenSchema}
        title={`View table for ${schema_name}`}
      />
    ),
  },
  {
    size: 500,
    accessorKey: 'schema_description',
    header: () => <T keyName='schema.table.headers.description' />,
    cell: ({ cell }) => {
      const description = cell.getValue<string>()
      return <DescriptionCell data={description} />
    },
  },
  {
    accessorKey: 'schema_owners',
    header: () => <T keyName='schema.table.headers.owners' />,
    cell: ({ cell }) => {
      const owners = cell.getValue<string[]>() || []

      if (owners.length === 0) {
        return <div className='text-sm'>-</div>
      }

      return <OwnersCell owners={owners} />
    },
  },
  {
    accessorKey: 'schema_version',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} titleKey='schema.table.headers.version'>
        <T keyName='schema.table.headers.version' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell }) => {
      const version = cell.getValue<string>()
      return <p className='text-sm'>{version || '-'}</p>
    },
  },
]

export { SchemaTable }
